import Chip from '@ben/orion/chip';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { memo, useCallback, useMemo } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import type { RateTableFields, StepsFields } from '../elements/types';

interface FormulaHelpChipsProps {
  className?: string;
  stepsControl?: Control<StepsFields>;
  rateTableControl?: Control<RateTableFields>;
}

interface FormulaComponent {
  key?: string;
  subForms?: { components?: FormulaComponent[] }[];
  components?: FormulaComponent[];
}

const FormulaHelpChips = ({
  className,
  stepsControl,
  rateTableControl,
}: FormulaHelpChipsProps) => {
  const steps = useWatch({
    control: stepsControl,
    name: 'additionalConfig.steps',
    exact: true,
  });
  const rateTables = useWatch({
    control: rateTableControl,
    name: 'additionalConfig.rateTables',
    exact: true,
  });

  const extractKeysFromComponents = useCallback(
    (components: FormulaComponent[]): string[] => {
      const keys: string[] = [];

      if (!components || !Array.isArray(components)) return keys;

      for (const component of components) {
        if (component.key) {
          keys.push(component.key);
        }

        if (component.subForms && Array.isArray(component.subForms)) {
          for (const subForm of component.subForms) {
            if (subForm.components && Array.isArray(subForm.components)) {
              keys.push(...extractKeysFromComponents(subForm.components));
            }
          }
        }

        if (component.components && Array.isArray(component.components)) {
          keys.push(...extractKeysFromComponents(component.components));
        }
      }

      return keys;
    },
    []
  );

  const componentKeys = useMemo(() => {
    const allKeys: string[] = [];

    if (steps && Array.isArray(steps)) {
      for (const step of steps) {
        if (step.components && Array.isArray(step.components)) {
          allKeys.push(...extractKeysFromComponents(step.components));
        }
      }
    }

    const uniqueKeys = [...new Set(allKeys)].filter(Boolean);

    return uniqueKeys.map((key) => ({
      key,
      label: key,
      category: 'Component Keys',
    }));
  }, [steps, extractKeysFromComponents]);

  const rateTableNames = useMemo(() => {
    if (!Array.isArray(rateTables))
      return [] as { key: string; label: string; category: string }[];
    return rateTables
      .filter((table) => table?.key)
      .map((table) => ({
        key: table.key,
        label: table.key,
        category: 'Rate Tables',
      }));
  }, [rateTables]);

  const handleCopyToClipboard = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success(`Copied "${key}" to clipboard`);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const hasComponentKeys = componentKeys.length > 0;
  const hasRateTableNames = rateTableNames.length > 0;

  if (!hasComponentKeys && !hasRateTableNames) {
    return (
      <FormulaHelpSection className={className}>
        <Typography variant={TypographyVariant.bodyMd2}>
          No component keys or rate tables available yet. Add steps with
          components or upload rate tables to see available keys.
        </Typography>
      </FormulaHelpSection>
    );
  }

  return (
    <FormulaHelpSection className={className}>
      <Typography variant={TypographyVariant.bodyMd2}>
        Available keys and rate tables (click to copy):
      </Typography>
      <ChipsContainer>
        {hasComponentKeys && (
          <ChipCategory>
            <Typography variant={TypographyVariant.bodySm2}>
              Component Keys
            </Typography>
            <ChipGroup>
              {componentKeys.map((item) => (
                <ChipWrapper key={item.key}>
                  <Chip
                    onClick={() => {
                      void handleCopyToClipboard(item.key).catch(console.error);
                    }}
                    checked={false}
                  >
                    {item.label}
                  </Chip>
                </ChipWrapper>
              ))}
            </ChipGroup>
          </ChipCategory>
        )}
        {hasRateTableNames && (
          <ChipCategory>
            <Typography variant={TypographyVariant.bodySm2}>
              Rate Tables
            </Typography>
            <ChipGroup>
              {rateTableNames.map((item) => (
                <ChipWrapper key={item.key}>
                  <Chip
                    onClick={() => {
                      void handleCopyToClipboard(item.key).catch(console.error);
                    }}
                    checked={false}
                  >
                    {item.label}
                  </Chip>
                </ChipWrapper>
              ))}
            </ChipGroup>
          </ChipCategory>
        )}
      </ChipsContainer>
    </FormulaHelpSection>
  );
};

export default memo(FormulaHelpChips);

const FormulaHelpSection = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const ChipsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const ChipCategory = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const ChipGroup = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
  `}
`;

const ChipWrapper = styled.div`
  ${({ theme }) => css`
    .mantine-Chip-root {
      height: 24px;
    }

    .mantine-Chip-label {
      height: 24px;
      padding: ${theme.spacing(0, 1.5)};
      font-size: ${theme.typography.bodySm1.fontSize};
    }
  `}
`;
